import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import TransferForm from "./TransferForm";
import "./Logistics.css";
import { useCombinedContext } from "../../context/CombinedContext";


const TransferList = () => {
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));

  const currentUser = authData?.role;

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [showForm, setShowForm] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;

  //--------------------------------------------------------
  // Load Transfers
  //--------------------------------------------------------

  const loadTransfers = async () => {
    try {
      setLoading(true);

      const params = {};

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axiosInstance.get(
        `/logistics/transfer/company/${companyId}`,
        {
          params,
        },
      );

      setTransfers(res.data.data || []);
    } catch (err) {
      toast.error("Failed to load transfers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadTransfers();
    }
  }, [companyId]);

  //--------------------------------------------------------
  // Date Filter
  //--------------------------------------------------------

  const handleFilter = () => {
    loadTransfers();
  };

  const clearFilter = () => {
    setStartDate("");
    setEndDate("");

    setTimeout(loadTransfers, 100);
  };

  //--------------------------------------------------------
  // Delete
  //--------------------------------------------------------

  const deleteTransfer = async (id) => {
    if (!window.confirm("Delete this transfer?")) return;

    try {
      await axiosInstance.delete(`/logistics/transfer/${id}`);

      toast.success("Transfer deleted");

      loadTransfers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  //--------------------------------------------------------
  // Request Approval
  //--------------------------------------------------------

  const requestApproval = async (id) => {
    try {
      await axiosInstance.patch(`/logistics/transfer/request-approval/${id}`);

      toast.success("Approval requested");

      loadTransfers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Request failed");
    }
  };

  //--------------------------------------------------------
  // Admin Approve
  //--------------------------------------------------------

  const approveTransfer = async (id) => {
    try {
      await axiosInstance.patch(`/logistics/transfer/approve/${id}`);

      toast.success("Approved");

      loadTransfers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approval failed");
    }
  };

  //--------------------------------------------------------
  // Search
  //--------------------------------------------------------

  const filteredTransfers = useMemo(() => {
    if (!search) return transfers;

    const keyword = search.toLowerCase();

    return transfers.filter((item) => {
      const remarks = item.remarks || "";

      const date = item.date ? new Date(item.date).toLocaleDateString() : "";

      const productNames =
        item.items?.map((x) => x.stock?.name || "").join(" ") || "";

      return (
        remarks.toLowerCase().includes(keyword) ||
        date.toLowerCase().includes(keyword) ||
        productNames.toLowerCase().includes(keyword)
      );
    });
  }, [transfers, search]);

  const currentStocksItems = filteredTransfers.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );
  const pageCount = Math.ceil(filteredTransfers.length / itemsPerPage);

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  const openAdd = () => {
    setSelectedTransfer(null);
    setShowForm(true);
  };

  //--------------------------------------------------------
  // Open Edit
  //--------------------------------------------------------

  const openEdit = (transfer) => {
    setSelectedTransfer(transfer);
    setShowForm(true);
  }; //--------------------------------------------------------
  // JSX
  //--------------------------------------------------------

  return (
    <div>
      <ToastContainer />

      <div className="mb-4 headStyle d-flex  justify-content-between align-items-center">
        <h4 className="mb-0">Stock Transfer</h4>

        <button
          className="button-2"
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
          onClick={openAdd}
        >
          + New Transfer
        </button>
      </div>

      <div className="headStyle mt-4 ">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </div>

            <div>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div>
              <button className="btn btn-success me-2" onClick={handleFilter}>
                Filter
              </button>

              <button className="btn btn-secondary" onClick={clearFilter}>
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Table */}

        <div className="my-3 table-responsive-sm" style={{ overflowX: "auto" }}>
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th>Date</th>
                <th>Products</th>
                <th>Total Qty</th>
                <th>Remarks</th>
                <th width="260">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : currentStocksItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Transfer Found
                  </td>
                </tr>
              ) : (
                currentStocksItems.map((transfer) => (
                  <tr key={transfer._id}>
                    <td>{new Date(transfer.date).toLocaleDateString()}</td>

                    <td>
                      {transfer.items?.map((item, index) => (
                        <div key={index}>
                          {item.stock?.name} ({item.quantity})
                        </div>
                      ))}
                    </td>

                    <td>
                      {transfer.items?.reduce(
                        (sum, item) => sum + Number(item.quantity),
                        0,
                      )}
                    </td>

                    <td>{transfer.remarks}</td>

                    <td>
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() => openEdit(transfer)}
                      >
                        Edit
                      </button>

                      <button
                        className="btn btn-danger btn-sm me-2"
                        onClick={() => deleteTransfer(transfer._id)}
                      >
                        Delete
                      </button>

                      {!transfer.editApproved &&
                        !transfer.editRequestPending &&
                        currentUser !== "admin" &&
                        currentUser !== "superadmin" && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => requestApproval(transfer._id)}
                          >
                            Request Approval
                          </button>
                        )}

                      {transfer.editRequestPending &&
                        (currentUser === "admin" ||
                          currentUser === "superadmin") && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => approveTransfer(transfer._id)}
                          >
                            Approve
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
        {/* Modal */}

        {showForm && (
          <TransferForm
            companyId={companyId}
            transfer={selectedTransfer}
            onClose={() => {
              setShowForm(false);
              loadTransfers();
            }}
            onSuccess={loadTransfers}
          />
        )}
      </div>
    </div>
  );
};

export default TransferList;
